import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Select,
  MenuItem,
  Slide,
} from '@mui/material';
import { useNavigate } from "react-router-dom";
import { styled, keyframes } from '@mui/system';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';


const slideInFromLeft = keyframes`
  0% {
    transform: translateX(-20px);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const AnimatedTypography = styled(Typography)(({ theme }) => ({
  animation: `${slideInFromLeft} 0.5s ease-out`,
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);


const CombinedTable = () => {
  const [loading, setLoading] = useState(true);
  const [combinedData, setCombinedData] = useState([]);
  const [user, setUser] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const navigate = useNavigate();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState('');

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
  };


  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        navigate("/");
        return;
      }

      try {
        const response = await axios.get("https://admin.bizstor.lk/public/api/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem("token");
          navigate("/");
        }
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

      const fetchWithRetry = async (url, retries = 3, delayMs = 1000) => {
        for (let i = 0; i < retries; i++) {
          try {
            const response = await axios.get(url);
            return response;
          } catch (error) {
            if (error.response?.status === 429) {
              const waitTime = delayMs * Math.pow(2, i);
              await delay(waitTime);
              continue;
            }
            if (i === retries - 1) throw error;
            await delay(delayMs);
          }
        }
      };

      try {
        const expensesResponse = await fetchWithRetry('https://admin.bizstor.lk/public/api/get-expense-data');
        await delay(500);

        const ordersResponse = await fetchWithRetry('https://admin.bizstor.lk/public/api/get-data');
        await delay(500);

        const expensespaymentResponse = await fetchWithRetry('https://admin.bizstor.lk/public/api/get-expensespayment');
        await delay(500);

        const orderspaymentResponse = await fetchWithRetry('https://admin.bizstor.lk/public/api/get-payment-data');
        await delay(500);

        const investmentpaymentResponse = await fetchWithRetry('https://admin.bizstor.lk/public/api/get-investment');
        await delay(500);

        const returnpaymentResponse = await fetchWithRetry('https://admin.bizstor.lk/public/api/get-damage-data');

        if (user.id) {
          const userExpenses = expensesResponse.data
            .filter(expense =>
              expense.usertoken?.toLowerCase() === user.id.toString().toLowerCase() &&
              expense.paymenttype === 'Paid'
            )
            .map(expense => {
              const totalAmount = expense.items.reduce((sum, item) => sum + Number(item.amount), 0);
              return {
                itemCategory: [...new Set(expense.items.map(i => i.category))].join(', '),
                credit: totalAmount,
                createdAt: expense.created_at,
                isExpense: true,
                fullname: expense.fullname,
                type: 'expense',
                id: expense.pay_id,
              };
            });

          const userOrders = ordersResponse.data
            .filter(order =>
              order.usertoken.toLowerCase() === user.id.toString().toLowerCase() &&
              order.term === 'due on receipt'
            )
            .map(order => {
              let orderTotal = 0;
              order.items.forEach(item => {
                const itemPrice = Number(item.rate) * Number(item.quantity);
                const itemTax = Number(item.tax) || 0;
                const itemTotalWithTax = itemPrice + (itemPrice * (itemTax / 100));
                orderTotal += itemTotalWithTax;
              });

              const discountPercentage = Number(order.discount) || 0;
              const discountAmount = (orderTotal * discountPercentage) / 100;
              const finalTotal = orderTotal - discountAmount;

              return {
                itemCategory: 'Accounts Receivable',
                debit: finalTotal || 0,
                createdAt: order.created_at,
                fullname: order.fullname || "Direct Customer",
                isExpense: false,
                type: 'order',
                id: order.pay_id,
              };
            });

          const userOrders2 = ordersResponse.data
            .filter(order =>
              order.usertoken.toLowerCase() === user.id.toString().toLowerCase() &&
              order.term !== 'due on receipt' &&
              order.payamount !== null
            )
            .map(order => ({
              itemCategory: 'Accounts Receivable',
              debit: Number(order.payamount),
              createdAt: order.created_at,
              fullname: order.fullname || "Direct Customer",
              isExpense: false,
              type: 'order',
              id: order.pay_id,
            }));

          const expensesPayments = expensespaymentResponse.data
            .filter(payment => payment.usertoken?.toLowerCase() === user.id.toString().toLowerCase())
            .map(payment => ({
              itemCategory: payment.item,
              credit: Number(payment.amount),
              createdAt: payment.created_at,
              fullname: payment.name,
              isExpense: true,
              type: 'expensespayment',
              id: payment.pay_id,
              memo: payment.memo,
            }));

          const orderPayments = orderspaymentResponse.data
            .filter(payment => payment.usertoken?.toLowerCase() === user.id.toString().toLowerCase())
            .map(payment => ({
              itemCategory: 'Accounts Receivable',
              debit: Number(payment.amount),
              createdAt: payment.created_at,
              fullname: payment.name,
              isExpense: false,
              type: 'order',
              id: payment.pay_id,
              memo: payment.memo,
            }));

          const returnPayment = returnpaymentResponse.data
            .filter(damages =>
              damages.usertoken.toLowerCase() === user.id.toString().toLowerCase() &&
              damages.type === 'money back'
            )
            .map(damages => {
              const totalAmount = damages.items.reduce(
                (sum, item) => sum + Number(item.quantity) * Number(item.rate),
                0
              );
              return {
                itemCategory: 'Refund',
                credit: totalAmount,
                createdAt: damages.created_at,
                fullname: damages.fullname,
                isExpense: false,
                type: 'return',
                id: damages.pay_id,
              };
            });

          const investmentPayments = investmentpaymentResponse.data.investment
            .filter(investment =>
              investment.usertoken?.toLowerCase() === user.id.toString().toLowerCase() &&
              investment.to === 'bank'
            )
            .map(investment => ({
              itemCategory: 'Investment Received',
              debit: Number(investment.amount),
              createdAt: investment.created_at,
              fullname: investment.person,
              isExpense: false,
              type: 'investment',
              id: investment.pay_id,
              memo: investment.memo || "",
            }));

          const investmentPaymentsPay = investmentpaymentResponse.data.investment
            .filter(investment =>
              investment.usertoken?.toLowerCase() === user.id.toString().toLowerCase() &&
              investment.from === 'bank'
            )
            .map(investment => ({
              itemCategory: 'Investment Payout',
              credit: Number(investment.amount),
              createdAt: investment.created_at,
              fullname: investment.person,
              isExpense: false,
              type: 'investment',
              id: investment.pay_id,
              memo: investment.memo || "",
            }));

          const combined = [
            ...userExpenses,
            ...userOrders,
            ...userOrders2,
            ...expensesPayments,
            ...orderPayments,
            ...returnPayment,
            ...investmentPayments,
            ...investmentPaymentsPay,
          ];

          combined.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setCombinedData(combined);
        }
      } catch (error) {
        if (error.response?.status === 429) {
          // Rate limit error
        } else if (error.response?.status >= 500) {
          // Server error
        } else {
          // General error
        }
      } finally {
        setLoading(false);
      }
    };

    if (user.id) {
      fetchData();
    }
  }, [user.id]);




  useEffect(() => {
    if (combinedData.length > 0) {
      const filtered = combinedData.filter(item => {
        const date = new Date(item.createdAt);
        const itemMonth = date.toLocaleDateString('en-US', { month: '2-digit' });
        const itemYear = date.getFullYear().toString();

        const matchMonth = selectedMonth ? itemMonth === selectedMonth : true;
        const matchYear = selectedYear ? itemYear === selectedYear : true;

        return matchMonth && matchYear;
      });

      setFilteredData(filtered);
      setPage(0); // Reset page when filter changes
    } else {
      setFilteredData([]);
    }
  }, [combinedData, selectedMonth, selectedYear]);


  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
  };

  const displayedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const getDisplayedRowsWithBalance = () => {
    const sortedData = [...displayedData].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    let balance = 0;
    const rowsWithBalance = sortedData.map((item) => {
      const debit = item.debit || 0;
      const credit = item.credit || 0;
      balance += debit - credit;
      return { ...item, balance };
    });

    // Show newest entries first
    return rowsWithBalance.reverse();
  };

  const dataWithBalance = getDisplayedRowsWithBalance();

  const handleBackClick = () => {
    navigate("/bank-account-history");
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: { sm: '95%', xs: '85%', },
          p: 2,
          bgcolor: 'background.paper',
          border: '1px solid #EDF2F7',
          borderRadius: '16px',
          margin: 'auto',
          mt: 3
        }}
      >

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'left',
              '@media (max-width: 600px)': {
                margin: 'auto',
              },

            }}
          >
            <Box sx={{ color: '#6d7081', }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  color: '#2D3748',
                  textAlign: 'center',
                  fontWeight: 700,
                  letterSpacing: '0.5px'
                }}
              >
                Transaction History
              </Typography>
            </Box>
            <Box
              component="div"
              sx={{
                mb: { sm: 2, xs: 'none' },
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                color: 'primary.main',
                width: 'fit-content',
                '&:hover': {
                  color: 'primary.main',
                }
              }}
              onClick={handleBackClick}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500
                }}
              >
                view report
              </Typography>
              <ArrowOutwardIcon sx={{ fontSize: '16px', ml: 0.5 }} />
            </Box>
          </Box>
        </Box>

        <Box sx={{ width: '100%', mb: 2, overflow: 'hidden' }}>
          <TableContainer style={{ marginTop: '20px' }}>
            <Table stickyHeader aria-label="combined data table" sx={{ p: 2 }}>
              <TableHead>
                <TableRow sx={{ '& td, & th': { padding: '8px 12px', bgcolor: '#F7FAFC', borderTopLeftRadius: '10px', borderTopRightRadius: '10px' } }}>
                  <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Transaction ID/Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase' }} align="right">Payment</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase' }} align="right">Deposit</TableCell>
                  {/**<TableCell sx={{ fontWeight: 'bold', textTransform:'uppercase'}} align="right">Balance</TableCell>*/}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow sx={{ '& td, & th': { padding: '8px 12px' } }}>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography variant="body1">No data found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  dataWithBalance.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                        <span style={{ color: 'green', fontWeight: 'bold' }}>#{item.id}</span><br></br>
                        {formatDate(item.createdAt)}
                      </TableCell>
                      <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                        <span style={{ color: 'text.secondary', fontWeight: 'bold' }}>{item.fullname}</span>
                        <br />
                        {item.itemCategory}
                      </TableCell>
                      <TableCell sx={{ color: 'red', fontSize: '14px', fontWeight: 'bold' }} align="right">
                        {item.credit ? Number(item.credit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                      </TableCell>
                      <TableCell sx={{ color: '#4299E1', fontSize: '14px', fontWeight: 'bold' }} align="right">
                        {item.debit ? Number(item.debit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                      </TableCell>
                      {/**<TableCell
                        sx={{
                          color: Number(item.balance) < 0 ? 'red' : 'text.secondary',
                          fontWeight: 'bold',
                          fontSize: '14px'
                        }}
                        align="right"
                      >
                        {Number(item.balance).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </TableCell>*/}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/**<Box
            display="flex"
            justifyContent="center"
            width="100%"
            mt={2}
          >
            <TablePagination
              rowsPerPageOptions={[100, 200, 300]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage=""
            />
          </Box>*/}
        </Box>
      </Box>
      {loading && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'white',
          zIndex: 9999
        }}>
          <l-trio
            size="50"
            speed="1.3"
            color="red"
          ></l-trio>
        </Box>
      )}
    </Box>
  );
};

export default CombinedTable;